import { type BrowserShortcut, browserShortcut } from '@crowdex/host-kit/shared/browser-shortcuts';
import {
  DesktopDropdownContent,
  DesktopDropdownLabel,
  DesktopDropdownRadioItem,
} from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  EyeOff,
  Globe,
  type LucideIcon,
  RotateCw,
  ScanSearch,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserAddressField,
  BrowserToolbar,
  ToolbarButton,
  ToolbarPill,
} from '@/components/browser/browser-toolbar';
import {
  type BrowserFindResult,
  type BrowserHistoryState,
  type BrowserPageControls,
  type BrowserSelection,
  NativeBrowserView,
} from '@/components/native-browser-view';
import { browserLabel, resolveBrowserInput, stepBrowserZoom } from '@/lib/browser-context';
import { isElectronDesktop } from '@/lib/electron-bridge';
import { isMacPlatform } from '@/lib/platform';
import { BROWSER_SESSION_MODES, type BrowserSessionMode } from '@/lib/side-panel/browser-session';

const SESSION_ICONS: Readonly<Record<BrowserSessionMode, LucideIcon>> = {
  shared: Users,
  isolated: ShieldCheck,
  private: EyeOff,
};

/** The tab's session menu: which profile (cookies, logins, storage) its pages use. */
function BrowserSessionMenu({
  mode,
  onChange,
}: {
  mode: BrowserSessionMode;
  onChange: (mode: BrowserSessionMode) => void;
}) {
  const Icon = SESSION_ICONS[mode];
  const current = BROWSER_SESSION_MODES.find((entry) => entry.mode === mode);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ToolbarButton
          aria-label={`Browser session: ${current?.label ?? mode}`}
          className={mode === 'shared' ? undefined : 'text-foreground'}
          title={current?.label}
        >
          <Icon className="size-3.5" />
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="end" className="w-64">
        <DesktopDropdownLabel>Session of this tab</DesktopDropdownLabel>
        <DropdownMenuRadioGroup
          onValueChange={(value) => onChange(value as BrowserSessionMode)}
          value={mode}
        >
          {BROWSER_SESSION_MODES.map((entry) => (
            <DesktopDropdownRadioItem key={entry.mode} value={entry.mode}>
              <span className="flex min-w-0 flex-col">
                <span>{entry.label}</span>
                <span className="type-caption text-muted-foreground">{entry.description}</span>
              </span>
            </DesktopDropdownRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

/** Searches the page (⌘F): Enter goes to the next match, ⇧Enter to the previous, Esc closes. */
function BrowserFindBar({
  onClose,
  onFind,
  result,
}: {
  onClose: () => void;
  onFind: (text: string, options: { forward: boolean; next: boolean }) => void;
  result: BrowserFindResult | null;
}) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);
  return (
    <ToolbarPill className="absolute top-1 right-3 z-20 gap-1 pl-3 shadow-md">
      <input
        aria-label="Find in page"
        className="h-7 w-44 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
        onChange={(event) => {
          setText(event.target.value);
          if (event.target.value.length > 0)
            onFind(event.target.value, { forward: true, next: false });
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
          else if (event.key === 'Enter' && text.length > 0)
            onFind(text, { forward: !event.shiftKey, next: true });
        }}
        placeholder="Find in page"
        ref={inputRef}
        value={text}
      />
      <span className="type-caption min-w-10 text-right text-muted-foreground tabular-nums">
        {text.length > 0 && result !== null ? `${result.active}/${result.matches}` : ''}
      </span>
      <ToolbarButton
        aria-label="Previous match"
        disabled={text.length === 0}
        onClick={() => onFind(text, { forward: false, next: true })}
      >
        <ChevronUp className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton
        aria-label="Next match"
        disabled={text.length === 0}
        onClick={() => onFind(text, { forward: true, next: true })}
      >
        <ChevronDown className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton aria-label="Close find bar" onClick={onClose}>
        <X className="size-3.5" />
      </ToolbarButton>
    </ToolbarPill>
  );
}

export function BrowserPane({
  active,
  selected,
  id,
  initialUrl,
  mode = 'shared',
  profileId,
  profile,
  controlled = false,
  onClose,
  onFaviconChange,
  onLabelChange,
  onModeChange,
  onNewTab,
  onPageFocus,
  onSelection,
  workspaceRoot,
}: {
  /** On screen (alone, or in a cell of the split). */
  active: boolean;
  /** The browser's current tab, which agents' commands without a tab id act on. */
  selected?: boolean;
  id: string;
  /** The page to open; without one the tab starts blank, on its address field. */
  initialUrl?: string;
  mode?: BrowserSessionMode;
  profileId?: string;
  /** A named isolated profile, kept for good. */
  profile?: string | undefined;
  controlled?: boolean;
  onFaviconChange?: (favicon: string | null) => void;
  onLabelChange: (label: string) => void;
  /** Closes this tab (⌘W). */
  onClose?: () => void;
  /** Opens a new tab beside it (⌘T). */
  onNewTab?: () => void;
  /** The page took the keyboard (clicked into, in the split). */
  onPageFocus?: () => void;
  /** Moves the tab to another session; the page stays. */
  onModeChange?: (mode: BrowserSessionMode) => void;
  onSelection: (selection: BrowserSelection) => void;
  workspaceRoot: string;
}) {
  const [url, setUrl] = useState(initialUrl ?? '');
  const [loadedUrl, setLoadedUrl] = useState(initialUrl ?? '');
  const [reloadKey, setReloadKey] = useState(0);
  const [selecting, setSelecting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [history, setHistory] = useState<BrowserHistoryState>({
    canGoBack: false,
    canGoForward: false,
  });
  const [loading, setLoading] = useState(false);
  const [finding, setFinding] = useState(false);
  const [findResult, setFindResult] = useState<BrowserFindResult | null>(null);
  const [zoom, setZoom] = useState(1);
  const pageRef = useRef<BrowserPageControls | null>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const pageTitleRef = useRef<string | null>(null);
  const native = isElectronDesktop();
  const blank = loadedUrl.length === 0;

  // The site's zoom, which another of its tabs may have changed: read again when shown and on
  // each page.
  const syncZoom = useCallback((): void => {
    const factor = pageRef.current?.zoomFactor();
    if (factor !== undefined) setZoom(factor);
  }, []);
  useEffect(() => {
    if (active) syncZoom();
  }, [active, syncZoom]);

  // A blank tab waits on its address field, as a browser's new tab does.
  useEffect(() => {
    if (active && blank) addressRef.current?.focus();
  }, [active, blank]);

  function loadUrl(): void {
    if (url.trim().length === 0) return;
    const nextUrl = resolveBrowserInput(url);
    setUrl(nextUrl);
    if (nextUrl === loadedUrl) setReloadKey((current) => current + 1);
    else setLoadedUrl(nextUrl);
    onLabelChange(browserLabel(nextUrl));
    // The page takes the keyboard, as in a browser once an address is entered.
    addressRef.current?.blur();
    pageRef.current?.focus();
  }

  function reload(): void {
    if (!blank) setReloadKey((current) => current + 1);
  }

  function zoomTo(factor: number): void {
    if (pageRef.current === null) return;
    setZoom(factor);
    pageRef.current?.setZoomFactor(factor);
  }

  function closeFind(): void {
    setFinding(false);
    setFindResult(null);
    pageRef.current?.stopFind();
    pageRef.current?.focus();
  }

  function onShortcut(shortcut: BrowserShortcut): void {
    if (shortcut === 'focus-address') addressRef.current?.focus();
    else if (shortcut === 'reload') reload();
    else if (shortcut === 'hard-reload') pageRef.current?.reloadIgnoringCache();
    else if (shortcut === 'back') pageRef.current?.goBack();
    else if (shortcut === 'forward') pageRef.current?.goForward();
    else if (shortcut === 'new-tab') onNewTab?.();
    else if (shortcut === 'close-tab') onClose?.();
    else if (shortcut === 'find') setFinding(true);
    else if (shortcut === 'zoom-in') zoomTo(stepBrowserZoom(zoom, 'in'));
    else if (shortcut === 'zoom-out') zoomTo(stepBrowserZoom(zoom, 'out'));
    else zoomTo(1);
  }

  function toggleSelection(): void {
    setSelectionError(null);
    setSelecting((current) => !current);
  }

  return (
    // An agent driving the tab never blocks the user (as in Codex): only its cursor and this state show.
    <section
      aria-busy={controlled}
      aria-label="Browser tab"
      className="relative flex h-full min-h-0 flex-col bg-background"
      // With the keyboard in the tab's own controls (or a blank tab, no page yet), the browser's
      // shortcuts are read here; from the page, the Desktop reads them (`browser-shortcut`).
      onKeyDown={(event) => {
        const shortcut = browserShortcut(
          {
            key: event.key,
            meta: event.metaKey,
            control: event.ctrlKey,
            shift: event.shiftKey,
            alt: event.altKey,
          },
          isMacPlatform(),
        );
        if (shortcut === null) return;
        event.preventDefault();
        event.stopPropagation();
        onShortcut(shortcut);
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <BrowserToolbar>
          <ToolbarPill>
            {native && (
              <>
                <ToolbarButton
                  aria-label="Back"
                  disabled={!history.canGoBack}
                  onClick={() => pageRef.current?.goBack()}
                  title="Back"
                >
                  <ArrowLeft className="size-3.5" />
                </ToolbarButton>
                <ToolbarButton
                  aria-label="Forward"
                  disabled={!history.canGoForward}
                  onClick={() => pageRef.current?.goForward()}
                  title="Forward"
                >
                  <ArrowRight className="size-3.5" />
                </ToolbarButton>
              </>
            )}
            {loading && native ? (
              <ToolbarButton
                aria-label="Stop loading"
                onClick={() => pageRef.current?.stop()}
                title="Stop"
              >
                <X className="size-3.5" />
              </ToolbarButton>
            ) : (
              <ToolbarButton aria-label="Reload" disabled={blank} onClick={reload} title="Reload">
                <RotateCw className="size-3.5" />
              </ToolbarButton>
            )}
          </ToolbarPill>
          <BrowserAddressField
            aria-label="Address"
            onChange={(event) => setUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') loadUrl();
              // Esc gives back the page's address, as in a browser.
              else if (event.key === 'Escape') {
                setUrl(loadedUrl);
                event.currentTarget.blur();
              }
            }}
            onSubmit={loadUrl}
            ref={addressRef}
            value={url}
          />
          {Math.abs(zoom - 1) > 0.001 && (
            <ToolbarPill>
              <button
                aria-label="Reset zoom"
                className="type-caption px-2 text-muted-foreground tabular-nums hover:text-foreground"
                onClick={() => zoomTo(1)}
                title="Reset zoom"
                type="button"
              >
                {Math.round(zoom * 100)}%
              </button>
            </ToolbarPill>
          )}
          <ToolbarPill>
            {native && onModeChange && <BrowserSessionMenu mode={mode} onChange={onModeChange} />}
            {native && (
              <ToolbarButton
                aria-label={
                  selecting ? 'Cancel element selection' : 'Select an element from the page'
                }
                aria-pressed={selecting}
                className={selecting ? 'bg-accent text-foreground' : undefined}
                onClick={toggleSelection}
                title={selectionError ?? 'Select an element to add it to the message'}
              >
                <ScanSearch className="size-3.5" />
              </ToolbarButton>
            )}
            <ToolbarButton
              aria-label="Open in a new window"
              disabled={blank}
              onClick={() => window.open(loadedUrl, '_blank', 'noopener,noreferrer')}
              title="Open in a new window"
            >
              <ExternalLink className="size-3.5" />
            </ToolbarButton>
          </ToolbarPill>
        </BrowserToolbar>
        <div className="relative mx-2 mb-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-hairline">
          {finding && native && !blank && (
            <BrowserFindBar
              onClose={closeFind}
              onFind={(text, options) => pageRef.current?.find(text, options)}
              result={findResult}
            />
          )}
          {blank ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-browser-canvas text-muted-foreground">
              <Globe className="size-8 opacity-40" />
              <p className="type-ui">Search or enter a URL above</p>
            </div>
          ) : native ? (
            <NativeBrowserView
              active={active}
              selected={selected ?? active}
              driven={controlled}
              id={id}
              mode={mode}
              onSelection={(selection) => {
                onSelection(selection);
              }}
              pageRef={pageRef}
              {...(onPageFocus ? { onFocus: onPageFocus } : {})}
              onLoadingChange={(next) => {
                setLoading(next);
                if (!next) syncZoom();
              }}
              onFindResult={setFindResult}
              onShortcut={onShortcut}
              // Private tabs keep no trace outside their own profile, so no favicon either.
              onFaviconChange={mode === 'private' ? undefined : onFaviconChange}
              // The page title names the tab once known; until then the URL does (below).
              onTitleChange={(title) => {
                pageTitleRef.current = title;
                if (title !== null) onLabelChange(title);
              }}
              onHistoryChange={setHistory}
              onSelectionModeError={setSelectionError}
              onUrlChange={(nextUrl) => {
                // The page's address now (a link, a redirect): what Esc gives back, and reloads.
                setUrl(nextUrl);
                setLoadedUrl(nextUrl);
                // In-page navigations keep the document, and so its title.
                onLabelChange(pageTitleRef.current ?? browserLabel(nextUrl));
              }}
              reloadKey={reloadKey}
              profileId={profileId}
              profile={profile}
              selectionEnabled={selecting}
              url={loadedUrl}
              workspaceRoot={workspaceRoot}
            />
          ) : (
            <iframe
              className="min-h-0 flex-1 border-0 bg-browser-canvas"
              sandbox="allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
              src={loadedUrl}
              title="Browser preview"
            />
          )}
        </div>
      </div>
    </section>
  );
}
